// src/pages/PrincipalDashboard.js
// v17 – disable legacy web first-run setup wizard; RN now owns onboarding

import React, { useEffect, useRef, useState } from "react";
import "../styles/PrincipalDashboard.css";
import "../styles/PrincipalDashboardPanels.css";
import "../styles/WebsiteApiKey.css";

import api from "../services/api";
import { API_PATHS } from "../config";

// Sections (self-contained components)
import ProfileCard from "../components/principal/ProfileCard";
import PhotosToReview from "../components/principal/PhotosToReview";
import StaffManager from "../components/principal/StaffManager";
import ParentInvite from "../components/principal/ParentInvite";
import WebsiteApiKey from "../components/principal/WebsiteApiKey";
import SubscriptionPanel from "../components/principal/SubscriptionPanel";
import YourPhotosGrid from "../components/principal/YourPhotosGrid";

const STORAGE_KEY = "principal-dashboard.activeTab";

const PrincipalDashboard = () => {
  const [profile, setProfile] = useState({
    email: "",
    displayName: "",
    schoolName: "",
    schoolWebsite: "",
    totalPhotos: 0,
    instagramUsername: null,
    schoolHashtag: null,
    websiteTechUsed: null,
    schoolId: null,
    principalName: null,
    hasInstalledMobileApp: false,

    // ✅ CMS FK snapshot fields from /profile
    cmsPlatformCode: null,
    cmsPlatformLabel: null,
    cmsBuilderCode: null,
    cmsBuilderLabel: null,
    cmsLastDetectedAt: null,
    cmsLastDetectionId: null,
  });

  const [loading, setLoading] = useState(true);
  const [instagramMessage, setInstagramMessage] = useState("");
  const [error, setError] = useState("");

  // Active tab — persist in localStorage
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved || "profile";
    } catch {
      return "profile";
    }
  });

  // Keep tabs mounted once opened (performance + state preservation)
  const [mountedTabs, setMountedTabs] = useState(new Set([activeTab]));

  // ✅ Helper: always merge patches (even for function updaters) so we never wipe snapshot fields
  const mergeProfile = (patchOrUpdater) => {
    setProfile((prev) => {
      const next =
        typeof patchOrUpdater === "function"
          ? patchOrUpdater(prev)
          : patchOrUpdater || {};
      return { ...prev, ...next };
    });
  };

  // ✅ Central mapping from API payload -> your local profile shape
  const mapProfileFromApi = (data) => {
    const emailPrefix = data?.email?.split("@")[0] || "";
    return {
      email: data?.email || "",
      displayName: data?.display_name || emailPrefix,
      schoolName: data?.school_name || "N/A",
      schoolWebsite: data?.school_website || "",
      totalPhotos: data?.total_photos || 0,
      instagramUsername: data?.instagram_username || "Not connected",
      schoolHashtag: data?.school_hashtag || null,
      websiteTechUsed: data?.website_tech_used || null,
      schoolId: data?.school_id || "No school ID assigned",
      principalName: data?.principal_name || "N/A",
      hasInstalledMobileApp: Boolean(data?.has_installed_mobile_app),

      // ✅ FK snapshot fields (preferred)
      cmsPlatformCode: data?.cms_platform_code || null,
      cmsPlatformLabel: data?.cms_platform_label || null,
      cmsBuilderCode: data?.cms_builder_code || null,
      cmsBuilderLabel: data?.cms_builder_label || null,
      cmsLastDetectedAt: data?.cms_last_detected_at || null,
      cmsLastDetectionId: data?.cms_last_detection_id || null,
    };
  };

  // ✅ Exposed refresh function (used by WebsiteApiKey “Refresh Status”)
  const refreshProfile = async () => {
    const { data } = await api.get(API_PATHS.PROFILE);
    const mapped = mapProfileFromApi(data);
    mergeProfile(mapped);
    return mapped;
  };

  const greeting = React.useMemo(() => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return "Good morning";
    if (h >= 12 && h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  // Fetch profile once on mount
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      try {
        const { data } = await api.get(API_PATHS.PROFILE);
        const mapped = mapProfileFromApi(data);

        // First load: set the full object (fine here)
        setProfile(mapped);
      } catch (e) {
        console.error("Failed to load principal profile:", e);
        setError("Failed to load your dashboard. Please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Handle return from Stripe/Instagram OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const messageText = params.get("message");
    const errorText = params.get("error");
    const errorDesc = params.get("error_description");

    if (success === "true" && messageText) {
      setInstagramMessage(decodeURIComponent(messageText));
      setTimeout(() => {
        setInstagramMessage("");
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 5000);
    } else if (errorText && errorDesc) {
      setError(decodeURIComponent(errorDesc));
      setTimeout(() => {
        setError("");
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 4000);
    }
  }, []);

  // Tab management
  const handleTabChange = (nextId) => {
    setActiveTab(nextId);
    setMountedTabs((prev) => new Set(prev).add(nextId));
    try {
      localStorage.setItem(STORAGE_KEY, nextId);
    } catch {
      // Ignore localStorage errors
    }
  };

  // Mobile select label helper (future-proof)
  const getTabText = (tab) => {
    if (typeof tab.label === "string") return tab.label;
    if (tab.id === "website") return "Your School’s Website";
    return tab.id;
  };

  const tabs = [
    {
      id: "profile",
      label: "Your Profile",
      render: () => (
        <ProfileCard
          profile={profile}
          onProfileChange={mergeProfile}
          onError={setError}
        />
      ),
    },
    {
      id: "photos-review",
      label: "Photos to Review",
      render: () => (
        <PhotosToReview onError={setError} isInstagramConnected={!!profile.instagramUsername && profile.instagramUsername !== "Not connected"} />
      ),
    },
    {
      id: "staff",
      label: "Staff Management",
      render: () => <StaffManager onError={setError} />,
    },
    {
      id: "parents",
      label: "Parent Management",
      render: () => <ParentInvite onError={setError} />,
    },
    {
      id: "website",
      label: "Your School’s Website",
      render: () => (
        <WebsiteApiKey
          active={activeTab === "website"}
          websiteTechUsed={profile.websiteTechUsed}

          // ✅ pass FK snapshot fields (preferred gating)
          cmsPlatformCode={profile.cmsPlatformCode}
          cmsPlatformLabel={profile.cmsPlatformLabel}
          cmsBuilderCode={profile.cmsBuilderCode}
          cmsBuilderLabel={profile.cmsBuilderLabel}
          cmsLastDetectedAt={profile.cmsLastDetectedAt}
          cmsLastDetectionId={profile.cmsLastDetectionId}

          schoolId={profile.schoolId}
          principalName={profile.principalName}
          schoolWebsite={profile.schoolWebsite}
          onSchoolWebsiteUpdated={(nextUrl) => mergeProfile({ schoolWebsite: nextUrl })}
          onError={setError}
          onProfileRefresh={refreshProfile}
        />
      ),
    },
    {
      id: "subscription",
      label: "Subscription",
      render: () => <SubscriptionPanel />,
    },
    {
      id: "your-photos",
      label: "Your Uploaded Photos",
      render: () => <YourPhotosGrid active={activeTab === "your-photos"} onError={setError} />,
    },
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-card">
          <div className="loading-spinner" />
          <p>Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="principal-dashboard-container page">
      <header className="principal-dashboard-header">
        <div className="principal-dashboard-heading">
          <p className="principal-dashboard-eyebrow">Principal dashboard</p>
          <h1 className="principal-dashboard-title">
            {greeting}, {profile.displayName}!
          </h1>
          <p className="principal-dashboard-subtitle">
            Manage staff, approve photos and connect your school website — all in one place.
          </p>
        </div>

        <div className="principal-dashboard-meta">
          {profile.schoolName && (
            <span className="principal-dashboard-school-chip">{profile.schoolName}</span>
          )}
          <div className="principal-dashboard-stat">
            <span className="principal-dashboard-stat-label">Photos shared</span>
            <span className="principal-dashboard-stat-value">{profile.totalPhotos}</span>
          </div>
        </div>
      </header>

      {instagramMessage && (
        <p className="success-message" aria-live="polite">
          {instagramMessage}
        </p>
      )}
      {error && (
        <p className="error-message" aria-live="polite">
          {error}
        </p>
      )}

      <section className="principal-dashboard-main">
        {/* Mobile tab selector */}
        <div className="principal-tabs-mobile">
          <label htmlFor="principal-tab-select" className="sr-only">
            Choose a section
          </label>
          <div className="principal-tabs-mobile-select-wrapper">
            <select
              id="principal-tab-select"
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value)}
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {getTabText(tab)}
                </option>
              ))}
            </select>
            <span className="principal-tabs-mobile-chevron" aria-hidden="true">
              ▾
            </span>
          </div>
        </div>

        {/* Desktop tabs */}
        <div className="principal-tabs-nav" role="tablist" aria-label="Principal dashboard sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              className={`principal-tab-button ${activeTab === tab.id ? "active" : ""}`}
              aria-selected={activeTab === tab.id}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab panels – keep-alive */}
        <div className="principal-tabs-content">
          {tabs.map((tab) => {
            if (!mountedTabs.has(tab.id)) return null;
            const isActive = tab.id === activeTab;
            return (
              <div
                key={tab.id}
                role="tabpanel"
                aria-hidden={!isActive}
                className={`principal-tab-panel ${isActive ? "active" : "hidden"}`}
              >
                {tab.render()}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default PrincipalDashboard;